import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { DemandRow } from './gap-analysis.utils';
import { competitionLabel, formatCompactVolume, formatCount } from './gap-analysis.utils';

interface Props {
  /** Already ranked and non-empty — the caller drops this panel entirely when
   *  no attribute matched a keyword, rather than rendering a blank one. */
  rows: DemandRow[];
}

/**
 * Which of the added attributes shoppers actually search for, ranked by monthly
 * volume. Competition is written out in words rather than colour-coded — a
 * green/amber/red ramp is not distinguishable under red-green colour blindness.
 */
export function GapDemandPanel({ rows }: Props) {
  // Rows arrive sorted descending, so the first sets the axis maximum.
  const scale = rows[0]?.searchVolume ?? 1;

  return (
    <div className="min-w-0 border-b p-5" data-testid="gap-analysis-demand-card">
      <h3
        className="text-foreground text-[13px] leading-5 font-semibold"
        data-testid="gap-analysis-demand-title"
      >
        New attributes by search volume
      </h3>
      <p className="text-muted-foreground mt-0.5 mb-4 text-xs">
        How often shoppers search for each new attribute, and how competitive that term is.
      </p>

      <div className="flex flex-col gap-2.5" data-testid="gap-analysis-demand-list">
        {rows.map((row) => {
          const competition = competitionLabel(row.competitionLevel);
          const isLowCompetition = row.competitionLevel?.toLowerCase() === 'low';

          return (
            <Tooltip key={`${row.aspect}-${row.tag}`}>
              <TooltipTrigger asChild>
                <div
                  className="grid cursor-help grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1"
                  data-testid={`gap-analysis-demand-item-${row.aspect}-${row.tag}`}
                >
                  <span className="truncate text-[12.5px] font-medium">{row.tag}</span>
                  <span
                    className="text-success text-xs font-semibold tabular-nums"
                    data-testid={`gap-analysis-demand-item-${row.aspect}-${row.tag}-value`}
                  >
                    {formatCompactVolume(row.searchVolume)}/mo
                  </span>
                  <div className="bg-muted col-span-2 h-[7px] overflow-hidden rounded">
                    <div
                      className="bg-success h-[7px] rounded"
                      style={{ width: `${String((row.searchVolume / scale) * 100)}%` }}
                    />
                  </div>
                  <div className="text-muted-foreground col-span-2 flex items-center gap-2 text-[11px]">
                    <span
                      data-testid={`gap-analysis-demand-item-${row.aspect}-${row.tag}-competition-badge`}
                      className={cn(
                        'rounded-full border px-1.5 py-px text-[10px] font-semibold tracking-wide uppercase',
                        isLowCompetition && 'border-success/35 bg-success-50 text-success',
                      )}
                    >
                      {competition ? `${competition} competition` : 'Competition unknown'}
                    </span>
                    <span className="capitalize">{row.aspect}</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {row.tag} — {formatCount(row.searchVolume)} monthly searches,{' '}
                {competition?.toLowerCase() ?? 'unknown'} competition
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
